import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class PerformanceMonitoringService implements OnDestroy {
	private readonly performanceMetrics = new BehaviorSubject<any>({});
	private performanceObserver: PerformanceObserver | null = null;

	constructor() {
		this.initializePerformanceMonitoring();
	}

	private initializePerformanceMonitoring(): void {
		// Monitor Core Web Vitals
		if ('PerformanceObserver' in globalThis) {
			try {
				this.performanceObserver = new PerformanceObserver((list) => {
					const entries = list.getEntries();
					entries.forEach((entry) => {
						this.trackMetric(entry);
					});
				});

				// Observe different types of performance entries
				this.performanceObserver.observe({
					entryTypes: [
						'navigation',
						'paint',
						'largest-contentful-paint',
						'first-input',
						'layout-shift'
					]
				});
			} catch (error) {
				console.warn('Performance monitoring not supported:', error);
			}
		}
	}

	private trackMetric(entry: PerformanceEntry): void {
		const currentMetrics = this.performanceMetrics.value;

		switch (entry.entryType) {
			case 'navigation':
				const navEntry = entry as PerformanceNavigationTiming;
				currentMetrics.navigation = {
					domContentLoaded:
						navEntry.domContentLoadedEventEnd -
						navEntry.domContentLoadedEventStart,
					loadComplete:
						navEntry.loadEventEnd - navEntry.loadEventStart,
					totalTime: navEntry.loadEventEnd - navEntry.fetchStart
				};
				break;

			case 'paint':
				const paintEntry = entry as PerformancePaintTiming;
				if (paintEntry.name === 'first-contentful-paint') {
					currentMetrics.firstContentfulPaint = paintEntry.startTime;
				}
				break;

			case 'largest-contentful-paint':
				currentMetrics.largestContentfulPaint = entry.startTime;
				break;

			case 'first-input':
				const firstInputEntry = entry as any;
				currentMetrics.firstInputDelay =
					firstInputEntry.processingStart - entry.startTime;
				break;

			case 'layout-shift':
				const layoutShiftEntry = entry as any;
				if (!layoutShiftEntry.hadRecentInput) {
					currentMetrics.cumulativeLayoutShift =
						(currentMetrics.cumulativeLayoutShift || 0) +
						layoutShiftEntry.value;
				}
				break;
		}

		this.performanceMetrics.next(currentMetrics);
	}

	/**
	 * Get current performance metrics
	 */
	getPerformanceMetrics(): Observable<any> {
		return this.performanceMetrics.asObservable();
	}

	/**
	 * Get current performance metrics synchronously
	 */
	getCurrentMetrics(): any {
		return this.performanceMetrics.value;
	}

	/**
	 * Measure custom performance
	 */
	measurePerformance(name: string, fn: () => void): number {
		const start = performance.now();
		fn();
		const end = performance.now();
		const duration = end - start;

		console.log(`Performance: ${name} took ${duration.toFixed(2)}ms`);
		return duration;
	}

	/**
	 * Measure async performance
	 */
	async measureAsyncPerformance<T>(
		name: string,
		fn: () => Promise<T>
	): Promise<T> {
		const start = performance.now();
		try {
			const result = await fn();
			const end = performance.now();
			const duration = end - start;
			console.log(
				`Async Performance: ${name} took ${duration.toFixed(2)}ms`
			);
			return result;
		} catch (error) {
			const end = performance.now();
			const duration = end - start;
			console.error(
				`Async Performance Error: ${name} failed after ${duration.toFixed(2)}ms`,
				error
			);
			throw error;
		}
	}

	/**
	 * Check if performance is good based on Core Web Vitals
	 */
	isPerformanceGood(): boolean {
		const metrics = this.getCurrentMetrics();

		// Core Web Vitals thresholds
		const goodFCP = metrics.firstContentfulPaint < 1800; // 1.8s
		const goodLCP = metrics.largestContentfulPaint < 2500; // 2.5s
		const goodFID = metrics.firstInputDelay < 100; // 100ms
		const goodCLS = metrics.cumulativeLayoutShift < 0.1; // 0.1

		return goodFCP && goodLCP && goodFID && goodCLS;
	}

	/**
	 * Get performance recommendations
	 */
	getPerformanceRecommendations(): string[] {
		const metrics = this.getCurrentMetrics();
		const recommendations: string[] = [];

		if (metrics.firstContentfulPaint > 1800) {
			recommendations.push(
				'First Contentful Paint is slow. Consider optimizing critical resources.'
			);
		}

		if (metrics.largestContentfulPaint > 2500) {
			recommendations.push(
				'Largest Contentful Paint is slow. Optimize images and critical resources.'
			);
		}

		if (metrics.firstInputDelay > 100) {
			recommendations.push(
				'First Input Delay is high. Reduce JavaScript execution time.'
			);
		}

		if (metrics.cumulativeLayoutShift > 0.1) {
			recommendations.push(
				'Cumulative Layout Shift is high. Ensure stable layout.'
			);
		}

		if (metrics.navigation?.totalTime > 3000) {
			recommendations.push(
				'Page load time is slow. Consider code splitting and lazy loading.'
			);
		}

		return recommendations;
	}

	/**
	 * Cleanup performance monitoring
	 */
	ngOnDestroy(): void {
		if (this.performanceObserver) {
			this.performanceObserver.disconnect();
		}
	}
}
